import { GroupBase, OptionsOrGroups, Select } from "chakra-react-select";
import { useController } from "react-hook-form";
import apiClient from "services/ApiClient";
import { DebouncedFunc, debounce } from "lodash";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { ControlledSelectProps, MENU_PORTAL_Z_INDEX, OptionType } from ".";

export interface GenericAsyncSelectProps<T> extends ControlledSelectProps {
  embedOption: (arg0: T) => Option<T>;
  extractValue: (arg0: T) => string;
  fetchPath: string;
  excludedIds?: string[];
  onChangeCallback?: (arg0: T | undefined) => void;
  cacheKey?: string;
  readOnly?: boolean;
  perPagePagination?: number;
  containerStyles?: object;
  resolveInitialValue?: (id: string) => Promise<Option<T> | undefined>;
}

interface Option<T> {
  value: string;
  label: string | ReactNode;
  rawValue: T;
}

async function fetchOptions<T>(
  path: string,
  extractValue: (arg0: T) => string,
  embedOption: (arg0: T) => Option<T>,
  inputValue: string,
  excludedIds: string[],
  perPagePagination?: number,
): Promise<OptionsOrGroups<Option<T>, GroupBase<Option<T>>>> {
  const separator = path.includes("?") ? "&" : "?";
  const pagination = perPagePagination ? `&pagination[per_page]=${perPagePagination}` : "";
  return apiClient
    .get<T[]>(`${path}${separator}q=${encodeURIComponent(inputValue)}${pagination}`)
    .then((value) => value.filter((v) => !(excludedIds || []).includes(extractValue(v))).map((v) => embedOption(v)))
    .catch(() => []);
}

export function GenericAsyncSelect<T>({
  control,
  name,
  id,
  placeholder,
  embedOption,
  extractValue,
  rules,
  fetchPath,
  excludedIds = [],
  perPagePagination = undefined,
  onChangeCallback = undefined,
  cacheKey = undefined,
  readOnly = false,
  variant,
  className,
  containerStyles = {},
  chakraStyles = {},
  resolveInitialValue = undefined,
  ...props
}: GenericAsyncSelectProps<T>) {
  const {
    field: { onChange, value: controlValue },
  } = useController({
    name,
    control,
    rules,
  });
  const { components, ...rest } = props;

  const {
    container: chakraContainer = {},
    option: chakraOption = {},
    valueContainer: chakraValueContainer = {},
    menu: chakraMenu = {},
    clearIndicator: chakraClearIndicator = {},
    crossIcon: chakraCrossIcon = {},
    ...restChakraStyles
  } = chakraStyles || {};

  type OptionsGroups = OptionsOrGroups<Option<T>, GroupBase<Option<T>>>;

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<OptionsGroups>([]);
  const [optionsCache, setOptionsCache] = useState<Record<string, OptionsGroups>>({});
  const deb = useRef<DebouncedFunc<() => Promise<void>> | undefined>();

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: OptionsGroups) => void) => {
      deb.current?.cancel();
      deb.current = debounce(async () => {
        setIsLoading(true);
        callback(
          await fetchOptions(fetchPath, extractValue, embedOption, inputValue, excludedIds || [], perPagePagination),
        );
        setIsLoading(false);
      }, 300);
      deb.current();
    },
    [fetchPath, extractValue, embedOption, excludedIds, perPagePagination],
  );

  useEffect(() => {
    const hydrateInitialOption = async () => {
      if (optionsCache[""]) {
        return;
      }
      if (!resolveInitialValue) {
        loadOptions("", (opts) => {
          setOptions(opts);
          setOptionsCache(opts ? { ...optionsCache, "": opts } : optionsCache);
        });
      } else {
        loadOptions("", async (defaultOptions) => {
          let mergedOptions = defaultOptions;

          const alreadyIncluded = defaultOptions.some((opt) => opt.value === controlValue);

          if (!alreadyIncluded && resolveInitialValue) {
            try {
              const resolved = await resolveInitialValue(controlValue);
              if (resolved) {
                mergedOptions = [resolved, ...defaultOptions];
              }
            } catch (e) {
              console.warn("Failed to resolve initial option", e);
            }
          }

          setOptions(mergedOptions);
          setOptionsCache((prev) => ({ ...prev, "": mergedOptions }));
        });
      }
    };

    hydrateInitialOption();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onInputChange = useCallback(
    (newValue: string) => {
      if (!newValue) {
        setOptions(optionsCache[""] || []);
        return;
      }
      if (optionsCache[newValue]) {
        setOptions(optionsCache[newValue]);
      } else {
        loadOptions(newValue, (opts) => {
          setOptions(opts || []);
          setOptionsCache(opts ? { ...optionsCache, [newValue]: opts } : optionsCache);
        });
      }
    },
    [loadOptions, optionsCache],
  );

  const handleOnChange = useCallback(
    (val: OptionType<T> | undefined) => {
      onChange(val?.value || "");
      if (onChangeCallback) onChangeCallback(val?.rawValue);
    },
    [onChange, onChangeCallback],
  );

  const value = useMemo(
    () => options.find((option) => (option as Option<T>).value === controlValue) as OptionType<T> | undefined,
    [options, controlValue],
  );

  const formVariant = variant;

  return (
    <Select
      key={cacheKey}
      name={name}
      menuPortalTarget={document.body}
      isLoading={isLoading}
      useBasicStyles
      // Options are already filtered server-side via onInputChange; the labels are JSX,
      // so react-select's default client filter would stringify them and match nothing.
      filterOption={() => true}
      isClearable={!readOnly}
      placeholder={formVariant === "floating" ? " " : placeholder}
      onChange={handleOnChange}
      options={options}
      value={value}
      onInputChange={onInputChange}
      components={{
        DropdownIndicator: null,
        ...components,
      }}
      variant={formVariant}
      classNamePrefix="chakra-react-select"
      className={clsx("chakra-react-select-container-single-select", className)}
      colorScheme="brand"
      chakraStyles={{
        container: (provided) => ({
          ...provided,
          cursor: "pointer",
          ...containerStyles,
          ...chakraContainer,
        }),
        option: (provided) => ({
          ...provided,
          color: "fg.muted",
          ...chakraOption,
        }),
        valueContainer: (provided) => ({
          ...provided,
          className: "single-select",
          ...chakraValueContainer,
        }),
        menu: (provided) => ({
          ...provided,
          marginTop: 0,
          ...chakraMenu,
        }),
        clearIndicator: (provided) => ({
          ...provided,
          width: "fit-content",
          height: "fit-content",
          marginRight: "1rem",
          _hover: {
            color: "cdsGrey.900",
          },
          ...chakraClearIndicator,
        }),
        crossIcon: (provided) => ({
          ...provided,
          width: "12px",
          height: "12px",
          ...chakraCrossIcon,
        }),
        ...restChakraStyles,
      }}
      styles={{
        menuPortal: (provided) => ({ ...provided, zIndex: MENU_PORTAL_Z_INDEX }),
        valueContainer: (provided) => ({
          ...provided,
          className: "single-select",
        }),
      }}
      {...rest}
    />
  );
}
